import {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  useCallback,
  useMemo,
  type ReactNode,
} from 'react';
import type { User } from '@lunit/shared-types';
import { coreRuntime } from '@core/runtime';
import { useIdentity } from '@core/contexts/IdentityContext';
import { isProfileGoneError, loadProfileWithRetry } from '@core/contexts/profileLoad';

/**
 * Профиль клиента (User с балансом/уровнем) — единый для обоих шеллов.
 * Форма контракта унаследована от mini-app UserContext ({user, loading, refresh}),
 * чтобы канонические страницы переезжали в ядро без переписывания.
 * Как именно грузится профиль (по telegramId или по uuid) — решает runtime.fetchProfile().
 */
interface ProfileContextValue {
  user: User | null;
  loading: boolean;
  /** Профиль не загрузился (после повторов временных сбоев). Экраны показывают ошибку, а не «гостя с нулём»
   * (K-041). При ошибке перечитывания уже загруженный профиль остаётся в user. */
  error: unknown;
  refresh: () => Promise<void>;
}

const ProfileContext = createContext<ProfileContextValue | null>(null);

export function ProfileProvider({ children }: { children: ReactNode }) {
  const identity = useIdentity();
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<unknown>(null);
  // Номер последнего запроса профиля: ответ более раннего (например, загрузки сразу после входа) не должен
  // затереть более свежий (перечитанный после согласия).
  const requestSeq = useRef(0);

  const identityKey = identity.telegramId ?? identity.userId;

  const refresh = useCallback(async () => {
    const seq = ++requestSeq.current;
    if (identity.status !== 'authenticated') {
      setUser(null);
      setError(null);
      setLoading(identity.status === 'loading');
      return;
    }
    const rt = coreRuntime();
    try {
      setLoading(true);
      // 429 (общий лимит по IP), 5xx и сеть — повтор с задержкой; устаревший запрос не повторяется.
      const profile = await loadProfileWithRetry(() => rt.fetchProfile(identity), {
        shouldContinue: () => seq === requestSeq.current,
      });
      if (seq === requestSeq.current) {
        setUser(profile);
        setError(null);
      }
    } catch (err) {
      console.error('[ProfileContext] Failed to load profile:', err);
      if (seq === requestSeq.current) {
        // Уже показанный профиль не подменяем «гостем»: user остаётся прежним (null — только при первой загрузке).
        setError(err);
        // PWA: uuid в localStorage от прошлой сессии или другого заведения, клиент удалён — выход на вход по номеру.
        if (isProfileGoneError(err) && rt.capabilities.logout && rt.logout) {
          void rt.logout();
        }
      }
    } finally {
      if (seq === requestSeq.current) setLoading(false);
    }
    // identityKey в deps ниже — перезагрузка при смене клиента
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [identity.status, identityKey]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const value = useMemo(() => ({ user, loading, error, refresh }), [user, loading, error, refresh]);

  return <ProfileContext.Provider value={value}>{children}</ProfileContext.Provider>;
}

export function useProfile(): ProfileContextValue {
  const ctx = useContext(ProfileContext);
  if (!ctx) {
    throw new Error('useProfile must be used within ProfileProvider');
  }
  return ctx;
}

/** Алиас формы mini-app UserContext — канонические страницы зовут useUser(). */
export const useUser = useProfile;
